import { useState } from 'react';
import { DialogLayout } from "@/subframe/layouts/DialogLayout";
import { Calendar } from "@/subframe/components/Calendar";
import { Button } from "@/subframe/components/Button";
import { FilterBadge } from "@/subframe/components/FilterBadge";
import moment from 'moment';

interface SelectDateAndTimePopupProps {
  open: boolean;
  setSelectDateAndTimePopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onScheduleMessage: (scheduledTime: number, messageText: string) => void;
  messageText: string;
}

interface TimePickerGridProps {
  title: string;
  options: string[];
  selectedValue: string;
  onSelect: (value: string) => void;
}

const TimePickerGrid = ({ title, options, selectedValue, onSelect }: TimePickerGridProps) => {
  return (
    <div className="bg-neutral-100 p-4 rounded-lg border border-solid border-neutral-border shadow-sm">
      <h3 className="text-center mb-3 text-body-bold font-body-bold text-default-font">{title}</h3>
      <div className="grid grid-cols-6 gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            className={`rounded-lg py-1 px-2 text-sm ${
              selectedValue === option
                ? "bg-brand-600 text-white"
                : "bg-neutral-200 text-default-font hover:bg-neutral-300"
            }`}
            onClick={() => onSelect(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};

function SelectDateAndTimePopup({
  open,
  setSelectDateAndTimePopupOpen,
  onScheduleMessage,
  messageText
}: SelectDateAndTimePopupProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedHour, setSelectedHour] = useState<string>("12");
  const [selectedMinute, setSelectedMinute] = useState<string>("00");
  const [selectedAmPm, setSelectedAmPm] = useState<string>("AM");
  
  const [showHourPicker, setShowHourPicker] = useState(false);
  const [showMinutePicker, setShowMinutePicker] = useState(false);
  
  // Create hours array: 01-12
  const hours = Array.from({ length: 12 }, (_, i) => 
    String(i + 1).padStart(2, '0')
  );
  
  // Create minutes array: 00-59 but display them in a grid
  const minutes = Array.from({ length: 60 }, (_, i) => 
    String(i).padStart(2, '0')
  );
  
  const handleHourClick = () => {
    setShowHourPicker(true);
    setShowMinutePicker(false);
  };
  
  const handleMinuteClick = () => {
    setShowHourPicker(false);
    setShowMinutePicker(true);
  };
  
  const handleHourSelect = (hour: string) => {
    setSelectedHour(hour);
    setShowHourPicker(false);
  };
  
  const handleMinuteSelect = (minute: string) => {
    setSelectedMinute(minute);
    setShowMinutePicker(false);
  };
  
  const toggleAmPm = () => {
    setSelectedAmPm(prev => prev === "AM" ? "PM" : "AM");
  };
  
  const handleScheduleSend = () => {
    // Create timestamp using moment.js
    const scheduledDateTime = moment(selectedDate);
    
    // Convert hour to 24-hour format if needed
    let hour = parseInt(selectedHour, 10);
    if (selectedAmPm === "PM" && hour < 12) {
      hour += 12;
    } else if (selectedAmPm === "AM" && hour === 12) {
      hour = 0;
    }
    
    // Set time
    scheduledDateTime.set({
      hour: hour,
      minute: parseInt(selectedMinute, 10),
      second: 0,
      millisecond: 0
    });
    
    // Convert to timestamp (milliseconds) - what we'll pass to the API
    const timestamp = scheduledDateTime.valueOf();    
    
    // Call the provided callback with timestamp and message
    onScheduleMessage(timestamp, messageText);
    setSelectDateAndTimePopupOpen(false);
  };

  return (
    <DialogLayout open={open} onOpenChange={() => {}}>
      <div className="flex h-full w-full flex-col items-start bg-default-background px-6 py-8">
        <div className="flex w-full flex-col items-start">
          <span className="w-full text-heading-2 font-heading-2 text-default-font text-center mb-4">
            Select date and time
          </span>
          
          <div className="flex w-full items-center">
            <div className="flex grow shrink-0 basis-0 flex-col items-start px-4 py-4">
              <div className="flex w-full grow shrink-0 basis-0 flex-col items-center justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date: Date | undefined) => date && setSelectedDate(date)}
                  initialFocus
                />
              </div>
            </div>
            
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4">
              <Button
                className="h-8 w-full flex-none"
                variant="neutral-secondary"
                icon="FeatherCalendar"
              >
                {moment(selectedDate).format('MMM D, YYYY')}
              </Button>
              
              {/* Time selection */}
              <div className="flex flex-col gap-3 w-full">
                <FilterBadge 
                  label={
                    <div className="flex items-center">
                      <button onClick={handleHourClick} className="px-1">{selectedHour}</button>
                      <span>:</span>
                      <button onClick={handleMinuteClick} className="px-1">{selectedMinute}</button>
                    </div>
                  } 
                  count={
                    <button onClick={toggleAmPm}>{selectedAmPm}</button>
                  }
                  className="w-full justify-center"
                />
                
                {showHourPicker && (
                  <TimePickerGrid
                    title="Choose Hour"
                    options={hours}
                    selectedValue={selectedHour}
                    onSelect={handleHourSelect}
                  />
                )}
                
                {showMinutePicker && (
                  <TimePickerGrid
                    title="Choose Minute"
                    options={minutes}
                    selectedValue={selectedMinute}
                    onSelect={handleMinuteSelect}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex w-full items-center justify-center gap-2 mt-6">
          <Button
            variant="neutral-secondary"
            onClick={() => setSelectDateAndTimePopupOpen(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleScheduleSend}>
            Schedule Send
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
}

export default SelectDateAndTimePopup;